import { GetString } from 'fluent-react';
import React from 'react';
import styled from 'styled-components';
import { demiFontWeight, normalFontWeight, semiBoldFontWeight } from '../../cssVariables';
import {
  CountryLookback,
  CountryProfile,
  Location,
  LookBackYearRange,
  PolicyRecommendation,
  Product,
  ProductClass,
} from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import { formatPercentage } from '../../numberFormatters';
import { failIfValidOrNonExhaustive } from '../../Utils';
import { getCountryName } from '../../Utils';
import {
  adjustRankValueDirection,
  NumberSign,
  numberToSign,
  primaryColor1,
} from '../Utils';
import { defaultECIProductClass } from '../Utils';

//#region styling
const smallBrowserHeight = 720;
const Root = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(3, auto);
  grid-row-gap: 1.25rem;
  grid-column-gap: 2rem;
  align-items: center;
  grid-auto-flow: row;
  border-bottom: 1px solid ${primaryColor1};
  padding-bottom: 1rem;

  @media (min-height: ${smallBrowserHeight}px) {
    padding-bottom: 3rem;
    grid-row-gap: 2.25rem;
  }
`;

const titleColor = '#333';

const HeadingText = styled.h1`
  font-size: 1.7rem;
  font-weight: ${demiFontWeight};
  text-align: center;
  grid-column: 1 / span 4;
  grid-row: 1;
  margin-bottom: 0;
  color: ${titleColor};

  @media (min-height: ${smallBrowserHeight}px) {
    font-size: 2rem;
  }
`;

const prevButtonSize = '2.8rem';

const PrevButton = styled.div`
  max-width: ${prevButtonSize};
  margin-top: auto;
  grid-row: 1;
  grid-column: 1;
  display: flex;
  align-items: flex-end;
  position: relative;

  svg {
    width: ${prevButtonSize};
    height: ${prevButtonSize};

    circle {
      fill: ${primaryColor1};
      stroke: none;
    }

    path {
      fill: #fff;
    }
  }

  &:hover {
    cursor: pointer;

    svg {
      circle {
        fill: #fff;
      }

      path {
        fill: ${primaryColor1};
      }
    }
  }
`;

const SectionText = styled.h2`
  text-transform: uppercase;
  font-size: 1.2rem;
  font-weight: ${normalFontWeight};
  grid-column: 1;
  color: ${titleColor};

  @media (min-height: ${smallBrowserHeight}px) {
    font-size: 1.375rem;
  }
`;

const WidgetContainerSingle = styled.div`
  grid-column: span 1;
`;

const WidgetContainerDouble = styled.div`
  grid-column: span 2;
`;

const WidgetContainerTriple = styled.div`
  grid-column: span 3;
`;

const TextBase = styled.div`
  text-transform: uppercase;
  color: ${primaryColor1};
  letter-spacing: 0.02em;
`;

const Indicator = styled(TextBase)`
  font-size: 2.2rem;
  margin-bottom: 0.5rem;
  font-weight: ${semiBoldFontWeight};

  @media (min-height: ${smallBrowserHeight}px) {
    font-size: 2.75rem;
  }
`;

const CenteredIndicator = styled(Indicator)`
  text-align: center;
`;

const IndicatorText = styled(TextBase)`
  font-size: 1.5rem;
  display: inline-block;
  margin-left: 0.5rem;

  @media (min-height: ${smallBrowserHeight}px) {
    font-size: 1.75rem;
  }
`;

const IconContainer = styled.div`
  display: inline-block;
`;
const EconomicStructureSvg = styled(IconContainer)`
  width: 2rem;
  margin-right: 0.5rem;

  .cls-1 {
    stroke: ${primaryColor1};
    stroke-width: 2;
    fill: none;
  }
`;

const InvertedSvg = styled(EconomicStructureSvg)`
  transform: rotate(180deg);
`;

const RibbonSvg = styled(IconContainer)`
  width: 1.25rem;
  position: relative;
  top: 7px;

  .cls-1 {
    stroke: ${primaryColor1};
    stroke-width: 2;
    fill: none;
  }
`;

const GraphSvg = styled(IconContainer)`
  width: 3rem;
  position: relative;
  top: 5px;
  margin-right: 0.75rem;

  .cls-5 {
    stroke-width: 0;
    fill: ${primaryColor1};
  }

  .cls-1 {
    stroke-width: 0;
    fill: none;
  }

  .cls-2 {
    stroke: ${primaryColor1};
    stroke-width: 0.7;
    fill: none;
  }
`;
//#endregion

const query = gql`
  query LandingPageText(
    $id: ID!, $yearRange: LookBackYearRange!, $year: Int!, $productClass: ProductClass!
  ) {
    location(id: $id) {
      id
      shortName
      nameAbbr
      thePrefix
    }
    countryProfile(location: $id) {
      growthProjection
      policyRecommendation
    }
    countryLookback(id: $id, yearRange: $yearRange, productClass: $productClass) {
      eciRankChange
    }
    newProductsCountry(location: $id, year: $year) {
      newProducts {
        id
      }
    }
  }
`;

interface SuccessResponse {
  location: null | {
    id: Location['id']
    shortName: Location['shortName']
    nameAbbr: Location['nameAbbr']
    thePrefix: Location['thePrefix'],
  };
  countryProfile: null | {
    growthProjection: CountryProfile['growthProjection'],
    policyRecommendation: CountryProfile['policyRecommendation'],
  };
  countryLookback: null | {
    eciRankChange: CountryLookback['eciRankChange'],
  };
  newProductsCountry: null | {
    newProducts: null | Array<{
      id: Product['id'],
    }>,
  };
}

interface Variables {
  id: string;
  yearRange: LookBackYearRange;
  year: number;
  productClass: ProductClass;
}

interface Props {
  countryId: string;
  getFluentString: GetString;
  year: number;
  goToPrevSlide: () => void;
}

const Text = (props: Props) => {
  const {
    countryId, getFluentString, year, goToPrevSlide,
  } = props;
  const yearRange = LookBackYearRange.TenYears;
  const variables: Variables = {
    id: countryId,
    yearRange,
    year,
    productClass: defaultECIProductClass,
  };
  const {loading, error, data} = useQuery<SuccessResponse, Variables>(query, {variables});
  let output: React.ReactElement<any> | null;
  if (loading === true) {
    output = null;
  } else if (error !== undefined) {
    console.error(error);
    output = null;
  } else if (data !== undefined) {
    const {location, countryLookback, countryProfile, newProductsCountry} = data;
    if (location === null || countryLookback === null || countryProfile === null || newProductsCountry === null) {
      output = null;
    } else {
      const {
        shortName, nameAbbr, thePrefix,
      } = location;
      const { growthProjection, policyRecommendation } = countryProfile;
      const { eciRankChange } = countryLookback;
      const newProducts = newProductsCountry && newProductsCountry.newProducts ? newProductsCountry.newProducts : [];
      const {nameSecondary} = getCountryName({shortName, nameAbbr, thePrefix: Boolean(thePrefix)});
      // Adjust ECI rank change so that the smallest number (position 1)
      // is considered the best instead of the largest number (last place)
      const eciRankChangeAdjustedDirection = adjustRankValueDirection(eciRankChange);
      const changeDirection = numberToSign(eciRankChangeAdjustedDirection);
      const growthPercentage = growthProjection !== null ? formatPercentage(growthProjection, 1) : 'N/A';
      let policyDimensionDescription: string;
      let policyName: string;
      if (policyRecommendation === null) {
        policyName = 'N/A';
        policyDimensionDescription = '';
      } else if (policyRecommendation === PolicyRecommendation.ParsimoniousIndustrial) {
        policyName = getFluentString('strategic-approach-name-parsimonious-industrial');
        policyDimensionDescription = getFluentString('conclusion-slide-text-parsimonious-industrial-description');
      } else if (policyRecommendation === PolicyRecommendation.StrategicBets) {
        policyName = getFluentString('strategic-approach-name-strategic-bets');
        policyDimensionDescription = getFluentString('conclusion-slide-text-strategic-bets-description');
      } else if (policyRecommendation === PolicyRecommendation.LightTouch) {
        policyName = getFluentString('strategic-approach-name-light-touch');
        policyDimensionDescription = getFluentString('conclusion-slide-text-light-touch-description');
      } else if (policyRecommendation === PolicyRecommendation.TechFrontier) {
        policyName = getFluentString('strategic-approach-name-tech-frontier');
        policyDimensionDescription = getFluentString('conclusion-slide-text-tech-frontier-description');
      } else {
        failIfValidOrNonExhaustive(policyRecommendation, 'Invalid policy ' + policyRecommendation);
        policyName = '';
        policyDimensionDescription = '';
      }
      const newProductCount = newProducts === null ? 0 : newProducts.length;
      const headingText = getFluentString('conclusion-slide-heading-text', {
        'country-name': shortName,
      });
      const countryComplexityChangeText = getFluentString('conclusion-slide-text-complexity-change', {
        'country-name': nameSecondary,
        'quantity': Math.abs(eciRankChange),
        'direction': changeDirection,
        'year-range': yearRange,
      });
      const newProductsText = getFluentString('conclusion-slide-text-new-products');
      const newProductsTextDescription = getFluentString('conclusion-slide-text-new-products-description', {
        'new-product-count': newProductCount,
      });
      const countryGrowthPercentageText = getFluentString('conclusion-slide-text-growth-percentage', {
        'country-name': nameSecondary,
        'growth-percentage': growthPercentage,
      });
      let arrowSvg: React.ReactElement<any> | null;
      if (changeDirection === NumberSign.Zero) {
        arrowSvg = null;
      } else if (changeDirection === NumberSign.Positive) {
        arrowSvg = (
          <EconomicStructureSvg
            dangerouslySetInnerHTML={{__html: require('./icons/arrow.svg')}}
          />
        );
      } else if (changeDirection === NumberSign.Negative) {
        arrowSvg = (
          <InvertedSvg
            dangerouslySetInnerHTML={{__html: require('./icons/arrow.svg')}}
          />
        );
      } else {
        failIfValidOrNonExhaustive(changeDirection, 'Invalid changeDirection ' + changeDirection);
        arrowSvg = null;
      }
      output = (
        <>
          <PrevButton
            onClick={goToPrevSlide}
            dangerouslySetInnerHTML={{__html: require('./icons/conclusion-up.svg')}}
          />
          <HeadingText>{headingText}</HeadingText>

          <SectionText>{getFluentString('conclusion-slide-label-economic-structure')}</SectionText>
          <WidgetContainerSingle>
            <CenteredIndicator>
              {arrowSvg}
              {Math.abs(eciRankChange)}
            </CenteredIndicator>
            <TextBase>{countryComplexityChangeText}</TextBase>
          </WidgetContainerSingle>
          <WidgetContainerDouble>
            <Indicator>
                <EconomicStructureSvg
                  dangerouslySetInnerHTML={{__html: require('./icons/plus-sign.svg')}}
                />
                {newProductCount}
                <IndicatorText>
                  {newProductsText}
                </IndicatorText>
            </Indicator>
            <TextBase>{newProductsTextDescription}</TextBase>
          </WidgetContainerDouble>

          <SectionText>{getFluentString('conclusion-slide-label-future-dynamics')}</SectionText>
          <WidgetContainerSingle>
            <CenteredIndicator>
              <GraphSvg
                dangerouslySetInnerHTML={{__html: require('../sectionNav/icons/nav-icon-4.svg')}}
              />
              {growthPercentage}
            </CenteredIndicator>
            <TextBase>{countryGrowthPercentageText}</TextBase>
          </WidgetContainerSingle>

          <SectionText>{getFluentString('conclusion-slide-label-paths-to-diversification')}</SectionText>
          <WidgetContainerTriple>
            <Indicator>
              <RibbonSvg
                dangerouslySetInnerHTML={{__html: require('./icons/ribbon.svg')}}
              />
              <IndicatorText>
                {policyName}
              </IndicatorText>
            </Indicator>
            <TextBase>{policyDimensionDescription}</TextBase>
          </WidgetContainerTriple>
        </>
      );
    }
  } else {
    output = null;
  }
  return (
    <Root>
      {output}
    </Root>
  );
};

export default Text;
